import React, { useEffect, useState } from "react";
import {
  X,
  Search,
  UploadCloud,
  ImageIcon,
  Music,
  FileText,
  Film,
  Copy,
  ExternalLink,
  Trash2, // افزودن آیکون حذف
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { listMedia, uploadMedia, deleteMedia } from "../../services/api/media";
import { toast } from "react-toastify";
import { CDN_BASE_URL } from "../../config";

const KINDS = ["", "image", "audio", "video", "document", "other"];

export default function MediaPickerModal({ isOpen, onClose, token }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  // upload form
  const [upName, setUpName] = useState("");
  const [upTags, setUpTags] = useState("");
  const [upFile, setUpFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    fetchMedia();
  }, [isOpen, kind]);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const res = await listMedia(token, { q, kind });
      const arr = res?.data?.data || res?.data || [];
      setItems(arr);
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    } finally {
      setLoading(false);
    }
  };

  const doUpload = async (e) => {
    e.preventDefault();
    if (!upFile) {
      toast.error(t("media_picker.toasts.choose_file"));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      if (upName) fd.append("name", upName);
      if (upTags) fd.append("tags", upTags);
      fd.append("file", upFile);

      const res = await uploadMedia(token, fd);
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(t("media_picker.toasts.uploaded"));
      setUpFile(null);
      setUpName("");
      setUpTags("");

      await fetchMedia();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteMedia(token, id);
      toast.success(t("media_picker.toasts.deleted"));
      await fetchMedia(); // لیست فایل‌ها را دوباره بارگذاری می‌کنیم
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  const iconFor = (it) => {
    if (it.kind === "image") return <ImageIcon className="w-4 h-4" />;
    if (it.kind === "audio") return <Music className="w-4 h-4" />;
    if (it.kind === "video") return <Film className="w-4 h-4" />;
    if (it.kind === "document") return <FileText className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("media_picker.toasts.copied"));
    } catch {
      toast.error(t("media_picker.toasts.copy_failed"));
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-5xl rounded-2xl shadow-xl p-4 md:p-6 max-h-[80vh] overflow-y-auto"
      >
        {/* header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg md:text-xl font-semibold">
            {t("media_picker.title")}
          </h2>
          <button
            className="p-2 rounded-lg hover:bg-gray-100"
            onClick={onClose}
            aria-label="close"
          >
            <X />
          </button>
        </div>

        {/* search & filter */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="flex items-center border rounded-lg px-3 py-2">
            <Search className="w-4 h-4 opacity-60" />
            <input
              className="flex-1 px-2 outline-none"
              placeholder={t("media_picker.search_ph")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchMedia()}
            />
          </div>
          <div>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              <option value="">{t("media_picker.kind_any")}</option>
              <option value="image">{t("media_picker.kind_image")}</option>
              <option value="audio">{t("media_picker.kind_audio")}</option>
              <option value="video">{t("media_picker.kind_video")}</option>
              <option value="document">
                {t("media_picker.kind_document")}
              </option>
              <option value="other">{t("media_picker.kind_other")}</option>
            </select>
          </div>
          <div className={`${isRtl ? "text-left" : "text-right"}`}>
            <button
              onClick={fetchMedia}
              className="px-3 py-2 rounded-lg border hover:bg-gray-50 text-sm"
            >
              {t("media_picker.search_btn")}
            </button>
          </div>
        </div>

        {/* upload form */}
        <div className="border rounded-xl p-3 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <UploadCloud className="w-4 h-4" />
            <h3 className="font-medium text-sm">
              {t("media_picker.upload_title")}
            </h3>
          </div>
          <form
            onSubmit={doUpload}
            className="grid grid-cols-1 md:grid-cols-4 gap-2 items-center"
          >
            <input
              type="file"
              onChange={(e) => setUpFile(e.target.files?.[0] || null)}
              className="md:col-span-2 border rounded-lg px-3 py-2"
              aria-label="file"
            />
            <input
              placeholder={t("media_picker.name_ph")}
              value={upName}
              onChange={(e) => setUpName(e.target.value)}
              className="border rounded-lg px-3 py-2"
            />
            <div className="flex gap-2">
              <input
                placeholder={t("media_picker.tags_ph")}
                value={upTags}
                onChange={(e) => setUpTags(e.target.value)}
                className="border rounded-lg px-3 py-2 flex-1"
              />
            </div>
            <button
              type="submit"
              disabled={uploading}
              className="px-3 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 text-sm"
            >
              {uploading
                ? t("media_picker.uploading")
                : t("media_picker.upload_btn")}
            </button>
          </form>
        </div>

        {/* list */}
        <div className="max-h-[50vh] overflow-auto border rounded-xl">
          {loading ? (
            <div className="p-4 text-sm text-gray-500">
              {t("media_picker.loading")}
            </div>
          ) : items.length === 0 ? (
            <div className="p-4 text-sm text-gray-500">
              {t("media_picker.empty")}
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((it) => (
                <li key={it._id} className="p-3 flex items-center gap-3">
                  {/* preview */}
                  <div className="w-14 h-14 flex items-center justify-center rounded border bg-white overflow-hidden">
                    {it.kind === "image" ? (
                      <img
                        src={CDN_BASE_URL + it?.url.replace("/", "")}
                        alt={it.name}
                        className="object-cover w-full h-full"
                      />
                    ) : (
                      <div className="text-gray-600">{iconFor(it)}</div>
                    )}
                  </div>

                  {/* info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {it.name}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {t(`media_picker.kind_${it.kind || "other"}`)} •{" "}
                      {it.mimeType || "-"} • {(it.tags || []).join(", ")}
                    </div>
                    <div className="text-xs text-gray-600 truncate mt-1">
                      {it.url}
                    </div>
                  </div>

                  {/* actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        copyUrl(CDN_BASE_URL + it?.url.replace("/", ""))
                      }
                      className="px-2 py-1 rounded border text-sm inline-flex items-center gap-1 hover:bg-gray-50"
                      title={t("media_picker.copy_url")}
                      type="button"
                    >
                      <Copy className="w-4 h-4" />
                      {t("media_picker.copy_url")}
                    </button>
                    <a
                      href={it.url}
                      target="_blank"
                      rel="noreferrer"
                      className="px-2 py-1 rounded border text-sm inline-flex items-center gap-1 hover:bg-gray-50"
                      title={t("media_picker.open")}
                    >
                      <ExternalLink className="w-4 h-4" />
                      {t("media_picker.open")}
                    </a>

                    {/* حذف فایل */}
                    <button
                      onClick={() => handleDelete(it._id)}
                      type="button"
                      className="px-2 py-1 rounded border text-sm inline-flex items-center gap-1 hover:bg-gray-50"
                      title={t("media_picker.delete")}
                    >
                      <Trash2 className="w-4 h-4" />
                      {t("media_picker.delete")}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
